import React, { type JSX, type RefObject, useCallback, useMemo } from "react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";

import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import { Table, TableHeadV2 } from "@vega-core/components/shadcn/table";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";

import type { EmptyCardProps } from "../../Cards/EmptyCard/EmptyCard";
import { MobileTable } from "../CoreTable/components/MobileTable";
import { ScrollIndicatorButton } from "../CoreTable/components/ScrollIndicatorButton";
import { TableBodyComponent } from "../CoreTable/components/TableBody";
import { TablePagination } from "../CoreTable/components/TablePagination";
import { useExpandableRows } from "../CoreTable/hooks/useExpandableRows";
import { useHorizontalScroll } from "../CoreTable/hooks/useHorizontalScroll";
import { usePinnedColumns } from "../CoreTable/hooks/usePinnedColumns";
import { useTableSelection } from "../CoreTable/hooks/useTableSelection";
import { useTableSorting } from "../CoreTable/hooks/useTableSorting";
import { createRowId } from "../CoreTable/utils/createRowId";
import {
  type ColumnGroup,
  type ExpandableRowConfig,
  type GroupedHeader,
  type setSortType,
} from "../types";

type SelectionConfig<TData> = {
  selectedRows: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  selectOnRowClick?: boolean;
  getCanSelect?: (row: TData) => boolean;
  hideUnselectableCheckboxes?: boolean;
  compactCheckbox?: boolean;
  disableResetSelectionOnDataChange?: boolean;
};

type ClassNames = {
  wrapper?: string;
  table?: string;
  row?: string;
  cell?: string;
  header?: string;
  groupHeader?: string;
  paginationWrapper?: string;
};

type GroupedHeaderTableProps<
  TData,
  TValue = unknown,
  S extends string = string,
> = {
  data: TData[];
  columnGroups: ColumnGroup<TData, TValue>[];
  setSort?: (sort: setSortType<S>) => void;
  initialSorting?: SortingState;
  isLoading?: boolean;
  onRowClick?: (row: TData) => void;
  noSorting?: boolean;
  classNames?: ClassNames;
  paginationElement?: JSX.Element | null;
  emptyState?: EmptyCardProps;
  selectionConfig?: SelectionConfig<TData>;
  getRowId?: (row: TData, index: number) => string;
  dataTestId: string;
  pinnedLeftColumns?: string[];
  expandableConfig?: ExpandableRowConfig<TData>;
  getRowClassName?: (row: TData) => string;
  getIsPinnedRow?: (row: TData) => boolean;
  ref?: RefObject<HTMLDivElement | null>;
  dimUnselectableRowsOnMobile?: boolean;
};

// border-separate lets us round individual cell corners (border-collapse ignores
// border-radius). All row/outline borders move from <tr> to cells.
const TABLE_CLASSES = [
  "border-separate border-spacing-0",

  // Regular header row (tr:last-child in thead): top + bottom borders
  "[&>thead>tr:last-child>th]:border-b",
  "[&>thead>tr:last-child>th]:border-t",
  "[&>thead>tr:last-child>th]:border-border/primary",

  // Body cells: bottom border (row separators + outer bottom)
  "[&>tbody>tr>td]:border-b",
  "[&>tbody>tr>td]:border-border/primary",

  // Outer left / right
  "[&>thead>tr:last-child>th:first-child]:border-l",
  "[&>thead>tr:last-child>th:last-child]:border-r",
  "[&>tbody>tr>td:first-child]:border-l",
  "[&>tbody>tr>td:last-child]:border-r",

  // Rounded outer corners
  "[&>thead>tr:last-child>th:first-child]:rounded-tl-containerCorner",
  "[&>thead>tr:last-child>th:last-child]:rounded-tr-containerCorner",
  "[&>tbody>tr:last-child>td:first-child]:rounded-bl-containerCorner",
  "[&>tbody>tr:last-child>td:last-child]:rounded-br-containerCorner",
].join(" ");

function computeGroupBorderColumns(
  groupedHeaders: GroupedHeader[],
): Map<number, string> {
  const borderMap = new Map<number, string>();
  let colIndex = 0;

  for (const group of groupedHeaders) {
    if (group.label.length > 0) {
      const firstCol = colIndex;
      const lastCol = colIndex + group.colSpan - 1;

      borderMap.set(firstCol, "border-l border-border/primary");

      const existing = borderMap.get(lastCol) ?? "";
      borderMap.set(
        lastCol,
        cnTwMerge(existing, "border-r border-border/primary"),
      );
    }
    colIndex += group.colSpan;
  }

  return borderMap;
}

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();
const EMPTY_COLUMN_VISIBILITY: VisibilityState = {};

const NOOP_EXPANDABLE_CONFIG = {
  isExpandable: () => false,
  getSubRows: (): never[] => [],
  getRowLevel: () => 0,
  getRowId: () => "",
};

export const GroupedHeaderTable = <
  TData,
  TValue = unknown,
  S extends string = string,
>({
  dataTestId,
  data,
  columnGroups,
  setSort,
  initialSorting,
  isLoading,
  onRowClick,
  noSorting = true,
  classNames,
  paginationElement,
  emptyState,
  selectionConfig,
  getRowId,
  pinnedLeftColumns,
  expandableConfig,
  getRowClassName,
  getIsPinnedRow,
  ref: externalRef,
  dimUnselectableRowsOnMobile = true,
}: GroupedHeaderTableProps<TData, TValue, S>) => {
  const columns = useMemo(
    () => columnGroups.flatMap((g) => g.columns),
    [columnGroups],
  );
  const groupedHeaders: GroupedHeader[] = useMemo(
    () =>
      columnGroups.map((g) => ({ label: g.label, colSpan: g.columns.length })),
    [columnGroups],
  );

  const enableRowSelection = selectionConfig !== undefined;
  const selectedRows = selectionConfig?.selectedRows;
  const onRowSelectionChange = selectionConfig?.onRowSelectionChange;
  const selectOnRowClick = selectionConfig?.selectOnRowClick ?? false;
  const getCanSelect = selectionConfig?.getCanSelect;
  const hideUnselectableCheckboxes =
    selectionConfig?.hideUnselectableCheckboxes ?? false;
  const compactCheckbox = selectionConfig?.compactCheckbox;

  const internalScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = externalRef ?? internalScrollRef;

  const { expandedData, expandableColumn, toggleRow, isExpandableToggle } =
    useExpandableRows<TData>(data, expandableConfig || NOOP_EXPANDABLE_CONFIG);

  const finalData = expandableConfig ? expandedData : data;

  const composedGetCanSelect = useMemo(() => {
    if (!getIsPinnedRow && !getCanSelect) return undefined;

    return (row: TData) => {
      if (getIsPinnedRow?.(row)) return false;

      return getCanSelect ? getCanSelect(row) : true;
    };
  }, [getIsPinnedRow, getCanSelect]);

  const { selectionColumn } = useTableSelection({
    data: finalData,
    enableRowSelection,
    selectedRows,
    onRowSelectionChange,
    getCanSelect: composedGetCanSelect,
    compactCheckbox,
    hideUnselectableCheckboxes: hideUnselectableCheckboxes || !!getIsPinnedRow,
    disableResetSelectionOnDataChange:
      selectionConfig?.disableResetSelectionOnDataChange,
  });

  const isMd = !useDeviceWidth("md");
  const { sorting, handleSortingChange } = useTableSorting<S>(
    setSort,
    initialSorting,
  );

  const { isScrolled, canScrollHorizontally } = useHorizontalScroll(
    scrollRef,
    finalData.length,
  );

  // Override the selection column header so it stays visible when all selectable
  // rows are checked (CoreTable's default hides it under hideUnselectableCheckboxes).
  const patchedSelectionColumn = useMemo(() => {
    const needsPatch = getIsPinnedRow || hideUnselectableCheckboxes;

    if (!selectionColumn || !needsPatch || !onRowSelectionChange)
      return selectionColumn;

    return {
      ...selectionColumn,
      header: ({
        table: t,
      }: {
        table: ReturnType<typeof useReactTable<TData>>;
      }) => {
        const allRows = t.getRowModel().rows;
        const selectableRows = composedGetCanSelect
          ? allRows.filter((row) => composedGetCanSelect(row.original))
          : allRows;

        if (selectableRows.length === 0) return null;

        const allSelected =
          selectableRows.length > 0 &&
          selectableRows.every((row) => row.getIsSelected());

        const onChange = (value: boolean) => {
          onRowSelectionChange((prev: RowSelectionState) => {
            if (value) {
              const next = { ...prev };
              for (const row of selectableRows) {
                next[row.id] = true;
              }

              return next;
            }

            const deselectedIds = new Set(selectableRows.map((row) => row.id));

            return Object.fromEntries(
              Object.entries(prev).filter(([id]) => !deselectedIds.has(id)),
            );
          });
        };

        return (
          <Checkbox
            aria-label="Select all rows"
            checked={allSelected}
            className="flex items-center justify-center"
            data-testid="select-all-checkbox"
            onCheckedChange={onChange}
          />
        );
      },
    } as typeof selectionColumn;
    // selectedRows is included so TanStack's column→cell memo chain invalidates
    // when selection changes, forcing cell components to re-render and pick up
    // the new row.getIsSelected() values via flexRender.
  }, [
    selectionColumn,
    getIsPinnedRow,
    hideUnselectableCheckboxes,
    composedGetCanSelect,
    onRowSelectionChange,
    selectedRows,
  ]);

  // Build final columns: utility columns + border-injected user columns
  const finalColumns = useMemo(() => {
    const borderMap = computeGroupBorderColumns(groupedHeaders);

    const modifiedColumns = columns.map((col, index) => {
      const classes = borderMap.get(index);

      if (!classes) return col;

      return {
        ...col,
        className: cnTwMerge(col.className, classes),
        cellHeaderClassName: cnTwMerge(col.cellHeaderClassName, classes),
      };
    });

    const result: ColumnDef<TData, TValue>[] = [];

    if (expandableConfig && expandableColumn?.id !== undefined) {
      result.push(expandableColumn as ColumnDef<TData, TValue>);
    }

    if (enableRowSelection && patchedSelectionColumn) {
      result.push(patchedSelectionColumn as ColumnDef<TData, TValue>);
    }

    result.push(...modifiedColumns);

    return result;
    // selectedRows is included so TanStack's column→cell memo chain invalidates
    // when selection changes, forcing cell re-renders via flexRender.
  }, [
    columns,
    groupedHeaders,
    expandableConfig,
    expandableColumn,
    enableRowSelection,
    patchedSelectionColumn,
    selectedRows,
  ]);

  const utilityColumnsCount =
    (expandableConfig ? 1 : 0) + (enableRowSelection ? 1 : 0);

  const table = useReactTable<TData>({
    data: finalData,
    columns: finalColumns,
    state: {
      sorting,
      columnVisibility: EMPTY_COLUMN_VISIBILITY,
      ...(selectedRows !== undefined && { rowSelection: selectedRows }),
    },
    ...(onRowSelectionChange && { onRowSelectionChange }),
    onSortingChange: handleSortingChange,
    getCoreRowModel: coreRowModel,
    getSortedRowModel: sortedRowModel,
    manualSorting: !!setSort,
    enableRowSelection,
    getRowId: getRowId || createRowId,
    getCanSelect: composedGetCanSelect,
    defaultColumn: {
      className: "w-auto",
    },
    renderFallbackValue: "-",
  });

  // Get rows from table, memoized to recalculate when data, selection, or sorting changes
  // This ensures consistent behavior with CoreTable and better performance
  const tableRows = useMemo(
    () => table.getRowModel().rows,

    [finalData, selectedRows, sorting],
  );

  const reorderedRows = useMemo(() => {
    if (!getIsPinnedRow) return tableRows;
    const pinned = tableRows.filter((r) => getIsPinnedRow(r.original));
    const rest = tableRows.filter((r) => !getIsPinnedRow(r.original));

    return [...pinned, ...rest];
  }, [tableRows, getIsPinnedRow]);

  const { getPinnedColumnProps } = usePinnedColumns({
    pinnedLeftColumns: pinnedLeftColumns ?? [],
    table,
  });

  const handleRowClickInternal = useCallback(
    (row: TData, rowId: string) => {
      const configRowId = expandableConfig
        ? expandableConfig.getRowId(row)
        : rowId;

      if (expandableConfig && !onRowClick && isExpandableToggle(configRowId)) {
        toggleRow(configRowId);
      }

      if (selectOnRowClick && enableRowSelection) {
        const canSelect = composedGetCanSelect
          ? composedGetCanSelect(row)
          : true;

        if (canSelect) {
          table.getRow(rowId).toggleSelected();
        }
      }

      if (onRowClick) {
        onRowClick(row);
      }
    },
    [
      table,
      onRowClick,
      selectOnRowClick,
      enableRowSelection,
      composedGetCanSelect,
      isExpandableToggle,
      toggleRow,
      expandableConfig,
    ],
  );

  const hasData = finalData.length > 0;
  const hasRowClick = Boolean(
    onRowClick || selectOnRowClick || enableRowSelection || expandableConfig,
  );

  if (isMd) {
    return (
      <MobileTable<TData, TValue>
        cellClassName={classNames?.cell}
        columnGroups={columnGroups}
        columns={columns}
        data={
          getIsPinnedRow ? data.filter((row) => !getIsPinnedRow(row)) : data
        }
        dataTestId={dataTestId ? `${dataTestId}-mobile` : undefined}
        dimUnselectableRowsOnMobile={dimUnselectableRowsOnMobile}
        emptyState={emptyState}
        enableRowSelection={enableRowSelection}
        expandableConfig={expandableConfig}
        getCanSelect={composedGetCanSelect}
        getRowId={getRowId}
        hasRowClick={hasRowClick}
        hideUnselectableCheckboxes={
          hideUnselectableCheckboxes || !!getIsPinnedRow
        }
        isLoading={isLoading}
        onRowClick={onRowClick}
        onRowSelectionChange={onRowSelectionChange}
        paginationElement={paginationElement}
        pinnedLeftColumns={pinnedLeftColumns}
        selectOnRowClick={selectOnRowClick}
        selectedRows={selectedRows}
        wrapperClassName={classNames?.wrapper}
      />
    );
  }

  const firstLabeledIdx = groupedHeaders.findIndex((g) => g.label.length > 0);
  const lastLabeledIdx = groupedHeaders.findLastIndex(
    (g) => g.label.length > 0,
  );

  return (
    <div
      className="w-full relative"
      data-testid={dataTestId || "grouped-header-table"}
    >
      <div
        className={cnTwMerge(
          "rounded-containerCorner overflow-hidden relative flex flex-1",
          pinnedLeftColumns && pinnedLeftColumns.length > 0
            ? "max-w-[100vw] overflow-x-auto"
            : "",
          classNames?.wrapper,
        )}
        ref={scrollRef}
        style={{
          overflowX:
            pinnedLeftColumns && pinnedLeftColumns.length > 0
              ? "auto"
              : undefined,
        }}
      >
        <Table
          className={cnTwMerge(
            "min-w-full w-full",
            TABLE_CLASSES,
            classNames?.table,
          )}
          data-testid={dataTestId ? `${dataTestId}-table` : "table"}
          style={{
            width: hasData ? table.getCenterTotalSize() : "100%",
          }}
        >
          {hasData ? (
            <thead data-testid="table-header">
              {/* Grouped header row */}
              {/* eslint-disable react/no-array-index-key -- static column order, so safe to use index as key */}
              <tr data-grouped-header="true">
                {Array.from({ length: utilityColumnsCount }, (_, i) => (
                  <th className="invisible !border-0" key={`spacer-${i}`} />
                ))}
                {groupedHeaders.map((group, index) => {
                  if (group.label.length === 0) {
                    return (
                      <th
                        className="invisible !border-0"
                        colSpan={group.colSpan}
                        key={`group-${index}`}
                      />
                    );
                  }

                  return (
                    <th
                      className={cnTwMerge(
                        "bg-background/secondary text-text/primary px-3 py-2 base-body-semibold text-left border-border/primary border-t border-l border-r",
                        index === firstLabeledIdx &&
                          "rounded-tl-companyBorderRadius",
                        index === lastLabeledIdx &&
                          "rounded-tr-companyBorderRadius",
                        classNames?.groupHeader,
                      )}
                      colSpan={group.colSpan}
                      key={`group-${index}`}
                    >
                      {group.label}
                    </th>
                  );
                })}
              </tr>
              {/* eslint-enable react/no-array-index-key */}
              {/* Regular header row, static column order so safe to use index as key */}
              <tr>
                {table.getFlatHeaders().map((header) => {
                  const {
                    isPinned,
                    isLastPinned,
                    leftOffset,
                    columnWidth,
                    columnMaxSize,
                    columnMinSize,
                  } = getPinnedColumnProps(header.id);

                  const headerTooltip =
                    header.column.columnDef.headerTooltip ?? "";
                  const cellHeaderClassName =
                    header.column.columnDef.cellHeaderClassName ?? "";
                  const noHeader = isFalsy(header.column.columnDef.header);

                  const hideSorting =
                    noSorting ||
                    !header.column.getCanSort() ||
                    header.column.columnDef.enableSorting === false;

                  const isMobileSelector =
                    header.column.columnDef.isMobileSelector;

                  const headerContent = flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  );

                  const shouldStick = isPinned && isScrolled;
                  const align = header.column.columnDef.align ?? "left";

                  return (
                    <TableHeadV2
                      align={align}
                      className={cnTwMerge(
                        {
                          "cursor-pointer select-none": !hideSorting,
                        },
                        cellHeaderClassName,
                        classNames?.header,
                        shouldStick && "sticky z-10 bg-background/subtle",
                      )}
                      data-testid={`header-${header.id}`}
                      hideSorting={hideSorting}
                      isLastPinned={isLastPinned}
                      isMobileSelector={isMobileSelector}
                      isScrolled={isScrolled}
                      key={header.id}
                      noHeader={noHeader}
                      onClick={() => {
                        if (noSorting) return;

                        if (!header.column.getCanSort()) return;
                        header.column.toggleSorting();
                      }}
                      sortedHeader={header.column.getIsSorted()}
                      style={
                        shouldStick
                          ? {
                              left: leftOffset,
                              width: columnWidth,
                              minWidth: columnMinSize,
                              maxWidth: columnMaxSize,
                            }
                          : {
                              width: header.getSize(),
                              minWidth: header.column.columnDef.minSize,
                              maxWidth: header.column.columnDef.maxSize,
                            }
                      }
                      tooltip={headerTooltip}
                    >
                      {headerContent}
                    </TableHeadV2>
                  );
                })}
              </tr>
            </thead>
          ) : null}
          <TableBodyComponent
            cellClassName={classNames?.cell}
            collapsed={false}
            columns={finalColumns}
            data={finalData}
            emptyState={emptyState}
            enableRowSelection={enableRowSelection}
            expandableConfig={expandableConfig}
            getCanSelect={composedGetCanSelect}
            getRowClassName={getRowClassName}
            hasRowClick={hasRowClick}
            isLoading={isLoading}
            isScrolled={isScrolled}
            onRowClick={handleRowClickInternal}
            pinnedLeftColumns={pinnedLeftColumns}
            rowClassName={classNames?.row}
            rows={reorderedRows}
            selectOnRowClick={selectOnRowClick}
            table={table}
          />
        </Table>
      </div>

      <ScrollIndicatorButton
        canScrollHorizontally={canScrollHorizontally}
        scrollRef={scrollRef}
      />

      <TablePagination
        paginationElement={paginationElement}
        wrapperClassName={classNames?.paginationWrapper}
      />
    </div>
  );
};
